import React, { useState, useMemo } from 'react';
import {
  TableHead,
  TableBody,
  TableCell,
  TableSortLabel,
  TextField,
  Box,
  Paper,
  Pagination,
  Stack,
} from '@mui/material';
import {
  StyledTableContainer,
  StyledTable,
  StyledTableHeadCell,
  StyledTableRow,
} from '../theme/tableStyles';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

const DataTable = ({
  columns,
  rows,
  actions,
  beforePagination = null,
  disableSorting = false,
  rowDragAndDrop = null,
}) => {
  const [order, setOrder] = useState('asc');
  const sortableColumns = columns.filter(c => c.sortable !== false);
  const [orderBy, setOrderBy] = useState(sortableColumns[0]?.id || '');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [draggingRowId, setDraggingRowId] = useState(null);

  const handleRequestSort = property => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  const filteredRows = useMemo(() => {
    if (!search) return rows;
    const lower = search.toLowerCase();
    return rows.filter(row =>
      columns.some(col =>
        String(row[col.id]).toLowerCase().includes(lower)
      )
    );
  }, [rows, search, columns]);

  const sortedRows = useMemo(() => {
    if (disableSorting || !orderBy) return filteredRows;
    return stableSort(filteredRows, getComparator(order, orderBy));
  }, [filteredRows, order, orderBy, disableSorting]);

  const paginatedRows = useMemo(
    () => sortedRows.slice((page - 1) * 10, (page - 1) * 10 + 10),
    [sortedRows, page]
  );

  const totalPages = Math.ceil(filteredRows.length / 10);

  return (
    <StyledTableContainer component={Paper}>
      <Box sx={{ p: 2 }}>
        <TextField
          value={search}
          onChange={e => setSearch(e.target.value)}
          label="Buscar"
          size="small"
          type="search"
          autoComplete="on"
        />
      </Box>
      <StyledTable>
        <TableHead>
          <StyledTableRow>
           {columns.map(col => (
            <StyledTableHeadCell
              key={col.id}
              sortDirection={orderBy === col.id ? order : false}
            >
              {col.sortable === false || disableSorting ? (
                col.label
              ) : (
                <TableSortLabel
                  active={orderBy === col.id}
                  direction={orderBy === col.id ? order : 'asc'}
                  onClick={() => handleRequestSort(col.id)}
                >
                  {col.label}
                </TableSortLabel>
              )}
            </StyledTableHeadCell>
          ))}
            {actions && <StyledTableHeadCell align="center">Acciones</StyledTableHeadCell>}
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {paginatedRows.map((row, index) => {
            const rowId = rowDragAndDrop?.getRowId ? rowDragAndDrop.getRowId(row) : (row.id ?? row._id ?? index);
            const isDraggable = Boolean(rowDragAndDrop?.enabled && rowDragAndDrop?.isRowDraggable?.(row));

            return (
            <StyledTableRow
              key={rowId}
              draggable={isDraggable}
              onDragStart={() => {
                if (!isDraggable) return;
                setDraggingRowId(rowId);
              }}
              onDragOver={event => {
                if (!isDraggable) return;
                event.preventDefault();
              }}
              onDrop={() => {
                if (!isDraggable || !draggingRowId || draggingRowId === rowId) return;
                rowDragAndDrop?.onRowDrop?.(draggingRowId, rowId);
                setDraggingRowId(null);
              }}
              onDragEnd={() => setDraggingRowId(null)}
              sx={{
                cursor: isDraggable ? 'grab' : 'default',
                backgroundColor: draggingRowId === rowId ? 'action.selected' : undefined
              }}
            >
              {columns.map(col => (
                <TableCell key={col.id}>{row[col.id]}</TableCell>
              ))}
              {actions && <TableCell align="center">{actions(row)}</TableCell>}
            </StyledTableRow>
          )})}
        </TableBody>
      </StyledTable>
      {beforePagination}
      <Stack alignItems="center" sx={{ my: 2 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handleChangePage}
          color="primary"
          showFirstButton
          showLastButton
        />
      </Stack>
    </StyledTableContainer>
  );
};

export default DataTable;
